import { MutableRefObject, useEffect } from 'react';
import { stopDefaultAndPropagation } from '../../../utils/domUtil';
import { getResizeTargetColumns } from '../utils/selectionUtils/tableSelectionResizeColumnUtils';
import { repositionAutofillHandle } from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import { getHotTableColumnWidthsPx } from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnResizeUtils';
import {
    applyManualColumnWidths,
    applyManualColumnWidthsWithHotHooks,
} from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnResizeApplyUtils';
import {
    clearManualColumnLiveResizeSession,
    getManualColumnLiveResizeSession,
    MilanoteHotTableCore,
} from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnLiveResizeSessionUtils';
import { createManualColumnResizerHandlesInDOM } from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnResizeDOMUtils';
import { getColumnWidthChangesAdjustedToGrid } from '../utils/dataUtils/columnSize/tableColumnResizeGridAlignmentUtils';
import { createManualColumnLiveResizeManager } from './manualColumnLiveResizeManager';
import { addHotTableColumnResizeUndoAction } from '../utils/hotInstanceUtils/undoRedo/hotTableUndoRedoColumnResizeUtils';

/**
 * Manages Milanote's custom column resize functionality.
 *
 * This functionality is actually provided by Handsontable's ManualColumnResize plugin,
 * but is not suitable for Milanote as:
 * - Positioning of the resize handle is problematic in a scrolled canvas
 * - User experience is lacking, as it doesn't live resize the column and table element
 *
 * We are still including Handsontable's ManualColumnResize plugin, since it provides the setManualSize function,
 * but the majority of the column resizing functionality is contained in this hook.
 */
export const useManualColumnResize = (props: {
    gridSize: number;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    hotTableInstanceRef: MutableRefObject<MilanoteHotTableCore | null>;
    getContextZoomScale: () => number;
}): void => {
    const { gridSize, hotTableContainerRef, hotTableInstanceRef, getContextZoomScale } = props;

    useEffect(() => {
        const hotInstance = hotTableInstanceRef.current;
        if (!hotInstance) return;

        const manualColumnLiveResizeManager = createManualColumnLiveResizeManager({ ...props, hotInstance });

        /**
         * Handles double-click handler for the column resize handle.
         * This will resize the column to fit the content
         */
        const onDoubleClickResize =
            (thElement: HTMLTableCellElement) =>
            (event: MouseEvent): void => {
                stopDefaultAndPropagation(event);

                if (getManualColumnLiveResizeSession(hotInstance).moved) return;
                clearManualColumnLiveResizeSession(hotInstance);

                const column = hotInstance.getCoords(thElement).col;
                if (column < 0) return;

                hotInstance.getActiveEditor()?.finishEditing();

                const zoomScale = getContextZoomScale();

                // Run Handsontable's beforeColumnResize hook on the target columns, this gives us the target widths
                const targetColumns = getResizeTargetColumns(hotInstance.getSelected(), column);
                const columnWidthsPx = [...getHotTableColumnWidthsPx(hotInstance)];
                const beforeColumnWidthsPx = [...columnWidthsPx];

                targetColumns.forEach((column) => {
                    const currentWidthPx = columnWidthsPx[column];
                    const hookWidthPx = hotInstance.runHooks('beforeColumnResize', currentWidthPx, column, true);

                    columnWidthsPx[column] = (hookWidthPx ?? currentWidthPx) / zoomScale;
                });

                // Adjust column width changes to make sure the table aligns with Milanote's grid
                const adjustedColumnWidthChanges = getColumnWidthChangesAdjustedToGrid(
                    columnWidthsPx,
                    targetColumns,
                    gridSize,
                    {
                        // Snap to next grid instead of nearest. It is better to
                        // overestimate than underestimate resize to fit content
                        snapToNextGrid: true,
                    },
                );

                const didApplyColumnWidths = applyManualColumnWidths(hotInstance, adjustedColumnWidthChanges);

                if (!didApplyColumnWidths) return;

                adjustedColumnWidthChanges.forEach(({ column, widthPx }) => {
                    const isDoubleClick = true;
                    hotInstance.runHooks('afterColumnResize', widthPx, column, isDoubleClick);
                });

                addHotTableColumnResizeUndoAction({
                    hotInstance,
                    beforeColumnWidthsPx,
                    columns: targetColumns,
                });

                repositionAutofillHandle(hotInstance, hotTableContainerRef);
            };

        const createManualColumnResizerHandles = () => {
            const handles = createManualColumnResizerHandlesInDOM(hotInstance);

            handles.forEach(({ thElement, resizeHandleElement }) => {
                resizeHandleElement.addEventListener('pointerdown', manualColumnLiveResizeManager.start(thElement));
                resizeHandleElement.addEventListener('dblclick', onDoubleClickResize(thElement));
            });
        };

        /**
         * Rebalances the last remaining column after a column removal so total table width stays grid-aligned.
         */
        const afterRemoveColumn = (): void => {
            createManualColumnResizerHandles();

            const columnWidthsPx = getHotTableColumnWidthsPx(hotInstance);
            const lastColIndex = columnWidthsPx.length - 1;

            if (lastColIndex < 0) return;

            // Adjust width of last column so that the table still aligns with Milanote's grid
            const adjustedColumnWidthChanges = getColumnWidthChangesAdjustedToGrid(
                columnWidthsPx,
                [lastColIndex],
                gridSize,
            );

            applyManualColumnWidthsWithHotHooks(hotInstance, adjustedColumnWidthChanges);

            repositionAutofillHandle(hotInstance, hotTableContainerRef);
        };

        hotInstance.addHook('afterViewRender', createManualColumnResizerHandles);
        hotInstance.addHook('afterCreateCol', createManualColumnResizerHandles);
        hotInstance.addHook('afterRemoveCol', afterRemoveColumn);

        createManualColumnResizerHandles();

        return () => {
            manualColumnLiveResizeManager.cleanup();

            hotInstance.removeHook('afterViewRender', createManualColumnResizerHandles);
            hotInstance.removeHook('afterCreateCol', createManualColumnResizerHandles);
            hotInstance.removeHook('afterRemoveCol', afterRemoveColumn);
        };
    }, [getContextZoomScale, gridSize, hotTableContainerRef, hotTableInstanceRef.current]);
};

export default useManualColumnResize;
